import {
  RecordingProcessStatus,
  transformRawDrives,
} from '@ouster/webviz-react-components';

import { useQuery } from '@tanstack/react-query';
import { compressToEncodedURIComponent } from 'lz-string';

import {
  UserOrg,
  DriveListApiResponse,
  RawDriveListApiResponse,
} from '../../types';
import { BASE_ORG_API } from '../../utils/constants';
import { parseUrlParams } from '../../utils/driveUtil';
import { fetchWithAuth } from '../../utils/requests';

// LocalStorage persistance
const LS_KEYS = {
  driveListPageSize: 'ouster.driveList.pageSize',
} as const;

const lsGetItem = (key: string): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const lsSetItem = (key: string, value: string): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch (e) {
    console.error('Failed to set localStorage item', e);
  }
};

// Returns the default page size from localStorage or a fallback.
export const getDefaultPageSize = (fallback = 200): number => {
  const raw = lsGetItem(LS_KEYS.driveListPageSize);
  const parsed = raw ? parseInt(raw, 10) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

// Persist the page size selection.
export const setDefaultPageSize = (size: number): void => {
  if (Number.isFinite(size) && size > 0) {
    lsSetItem(LS_KEYS.driveListPageSize, String(size));
  }
};

interface QueryConfig<T = any> {
  endpoint: string;
  defaultOrderBy: string;
  requiresOrg: boolean;
  useCompression?: boolean;
  transformData?: (data: T[]) => T[];
}

type TransformedDriveData = ReturnType<typeof transformRawDrives>;

const QUERY_CONFIGS: Record<string, QueryConfig<TransformedDriveData>> = {
  sharedWith: {
    endpoint: 'shared_drives/',
    defaultOrderBy: 'shared_time_stamp/',
    requiresOrg: true,
  },
  sharedFrom: {
    endpoint: 'shared_drives/shared/',
    defaultOrderBy: 'shared_time_stamp',
    requiresOrg: true,
  },
  fullyPublic: {
    endpoint: 'api/v1/shared_drives/public_public',
    defaultOrderBy: 'updated_at',
    requiresOrg: false,
  },
  privatePublic: {
    endpoint: 'shared_drives/private_public',
    defaultOrderBy: 'updated_at',
    requiresOrg: true,
  },
  private: {
    endpoint: 'recordings/',
    defaultOrderBy: 'processed_at',
    requiresOrg: true,
    useCompression: true,
    transformData: (data: TransformedDriveData[]) =>
      data.map((drive) => {
        // if the drive has existed before the verification feature introduced, they are LEGACY
        if (drive.visualization_status === 'LEGACY') return drive;

        // if the drive is failed, no matter it is verified or not, we keep the status as Failed
        if (drive.status === RecordingProcessStatus.Failed) {
          return drive;
        }

        // when the drive is not failed and the visualization status is UNKNOWN
        if (drive.visualization_status === 'UNKNOWN') {
          const newStatus = RecordingProcessStatus.Processing;
          return { ...drive, status: newStatus };
        }

        // when the drive is ready and the visualization status is NOT_VIEWABLE
        if (
          drive.visualization_status === 'NOT_VIEWABLE' &&
          drive.status === RecordingProcessStatus.Ready
        ) {
          const newStatus = RecordingProcessStatus.VizAlert;
          return { ...drive, status: newStatus };
        }
        return drive;
      }),
  },
};

// Generic function to fetch drives list based on query type
const fetchDrivesList = async (
  queryType: keyof typeof QUERY_CONFIGS,
  curUserOrg: UserOrg | undefined,
  orderBy: string,
  sortOrder: string,
  pageIndex: number,
  pageSize: number,
  searchParams: string
): Promise<DriveListApiResponse> => {
  const config = QUERY_CONFIGS[queryType];

  if (config.requiresOrg && !curUserOrg?.id) {
    throw new Error('Organization ID is not available');
  }

  const filters = parseUrlParams(searchParams);
  const page = `${pageIndex + 1}` || '1';

  // Build URL based on query type
  let fetchURL: URL;
  if (config.requiresOrg && queryType !== 'fullyPublic' && curUserOrg?.id) {
    fetchURL = new URL(
      `${BASE_ORG_API}/${curUserOrg.id}/${config.endpoint}`,
      window.location.origin
    );
  } else {
    fetchURL = new URL(config.endpoint, window.location.origin);
  }

  // Handle different parameter formats
  if (config.useCompression) {
    // Private list uses compression
    const queryParams = {
      page: parseInt(page, 10),
      per_page: pageSize,
      order_by: orderBy || config.defaultOrderBy,
      sort_order: sortOrder,
      filter: {
        data_type: ['SDK'],
      } as Record<string, string[]>,
    };

    Object.entries(filters).forEach(([key, values]) => {
      if (key === 'order_by' || key === 'data_type' || key === 'sort_order')
        return;

      values.forEach((value) => {
        const filterKey = key === 'tags' ? 'tag' : key;
        if (!queryParams.filter[filterKey]) {
          queryParams.filter[filterKey] = [];
        }
        queryParams.filter[filterKey].push(value);
      });
    });

    const compressedQuery = compressToEncodedURIComponent(
      JSON.stringify(queryParams)
    );
    fetchURL.searchParams.set('query', compressedQuery);
  } else {
    // Standard query params for other endpoints
    fetchURL.searchParams.set('page', page);
    fetchURL.searchParams.set('per_page', `${pageSize}`);
    fetchURL.searchParams.set('order_by', orderBy || config.defaultOrderBy);
    fetchURL.searchParams.set('sort_order', sortOrder);
    fetchURL.searchParams.set('filter', 'data_type=SDK');

    Object.entries(filters).forEach(([key, values]) => {
      if (key === 'order_by' || key === 'data_type' || key === 'sort_order')
        return;

      values.forEach((value) =>
        fetchURL.searchParams.append(
          'filter',
          `${key === 'tags' ? 'tag' : key}=${value}`
        )
      );
    });
  }

  const response = await fetchWithAuth(fetchURL.href);
  const json = (await response.json()) as RawDriveListApiResponse;
  let transformedData: TransformedDriveData[] = json.data.map((drive) =>
    transformRawDrives(drive)
  );

  // Apply custom transformation if provided
  if (config.transformData) {
    transformedData = config.transformData(transformedData);
  }

  return {
    ...json,
    data: transformedData,
    meta: {
      totalCount: json.total_count,
    },
  };
};

// Generic fetch function

const createDriveListQuery =
  (
    queryType: keyof typeof QUERY_CONFIGS,
    queryKey: string,
    refetchOnWindowFocus = false
  ) =>
  (
    curUserOrg: UserOrg | undefined,
    orderBy: string,
    sortOrder: string,
    pageIndex: number,
    pageSize: number,
    searchParams: string
  ) => {
    const config = QUERY_CONFIGS[queryType];

    const {
      data: { data = [], meta } = {},
      isError,
      isRefetching,
      isLoading,
      isSuccess,
      refetch,
    } = useQuery<DriveListApiResponse>({
      queryKey: [
        queryKey,
        ...(config.requiresOrg ? [curUserOrg?.id] : []),
        orderBy,
        sortOrder,
        pageIndex,
        pageSize,
        searchParams,
      ],
      queryFn: () =>
        fetchDrivesList(
          queryType,
          curUserOrg,
          orderBy,
          sortOrder,
          pageIndex,
          pageSize,
          searchParams
        ),
      staleTime: Infinity,
      gcTime: 900000, // Data is cached for 15 minutes
      refetchOnWindowFocus,
      refetchOnMount: false,
      enabled: !config.requiresOrg || !!curUserOrg?.id,
    });

    return {
      data,
      meta,
      isError,
      isRefetching,
      isLoading,
      isSuccess,
      refetch,
    };
  };

// Export the specific hooks
export const useSharedWithOusterListQuery = createDriveListQuery(
  'sharedWith',
  'shared-with-list',
  false
);

export const usePrivatePublicListQuery = createDriveListQuery(
  'privatePublic',
  'private-public-drive-list',
  false
);

export const useSharedFromOusterListQuery = createDriveListQuery(
  'sharedFrom',
  'shared-from-list',
  false
);

export const useFullyPublicListQuery = createDriveListQuery(
  'fullyPublic',
  'public-public-drive-list',
  true
);

export const usePrivateListQuery = createDriveListQuery(
  'private',
  'drive-list',
  true
);
